import React from 'react';
import {
  IAlertThresholdCompareOp,
  IAlertThresholdCustom,
  IComparisonConfig,
  LogicalOperatorKey,
} from '../../../utils/apollo/graphqlSchema';
import styled, { css } from 'styled-components';
import CustomThreshold from './CustomThreshold';
import { SingleSelectMenu } from '../../Dropdown';

interface IProps {
  comparisonConfig: IComparisonConfig | null;
  compareOps: IAlertThresholdCompareOp[];
  firstCustomThreshold: IAlertThresholdCustom;
  setFirstCustomThreshold: (newThreshold: IAlertThresholdCustom) => void;
  secondCustomThreshold: IAlertThresholdCustom | null;
  setSecondCustomThreshold: (newThreshold: IAlertThresholdCustom) => void;
  removeSecondCustomThreshold: () => void;
  logicOpKey: LogicalOperatorKey | null;
  setLogicOpKey: (newKey: LogicalOperatorKey) => void;
}

const CustomThresholdsRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

const LogicOpsSingleSelectMenu = styled(SingleSelectMenu)<{
  $bottomSpaced: boolean;
}>`
  ${({ $bottomSpaced }) =>
    $bottomSpaced &&
    css`
      margin-bottom: 14px;
    `};
`;

const logicOptions = [
  {
    text: LogicalOperatorKey.And,
    value: LogicalOperatorKey.And,
    tooltip: { content: 'Alert will be triggered if both conditions are true' },
  },
  {
    text: LogicalOperatorKey.Or,
    value: LogicalOperatorKey.Or,
    tooltip: {
      content: 'Alert will be triggered if any of the conditions is true',
    },
  },
];

export default ({
  comparisonConfig,
  compareOps,
  firstCustomThreshold,
  setFirstCustomThreshold,
  secondCustomThreshold,
  setSecondCustomThreshold,
  removeSecondCustomThreshold,
  logicOpKey,
  setLogicOpKey,
}: IProps) => {
  const handleLogicOpChange = (value: string) => {
    if (
      Object.values(LogicalOperatorKey).includes(value as LogicalOperatorKey)
    ) {
      setLogicOpKey(value as LogicalOperatorKey);
    }
  };

  const handleAddThreshold = () => {
    if (secondCustomThreshold) {
      return; // already have a second entry
    }
    handleLogicOpChange(logicOptions[0].value);
  };

  return (
    <CustomThresholdsRow>
      <CustomThreshold
        comparisonConfig={comparisonConfig}
        compareOps={compareOps}
        threshold={firstCustomThreshold}
        setThreshold={setFirstCustomThreshold}
        allowAddMore={!logicOpKey}
        onAdd={handleAddThreshold}
      />
      {logicOpKey && (
        <LogicOpsSingleSelectMenu
          $bottomSpaced={
            firstCustomThreshold.compareOp.requireComparison ?? false
          }
          options={logicOptions}
          value={logicOpKey}
          onChange={handleLogicOpChange}
          customWidth="70px"
        />
      )}
      {secondCustomThreshold && (
        <CustomThreshold
          comparisonConfig={comparisonConfig}
          compareOps={compareOps}
          threshold={secondCustomThreshold}
          setThreshold={setSecondCustomThreshold}
          onRemove={removeSecondCustomThreshold}
          allowAddMore={false}
        />
      )}
    </CustomThresholdsRow>
  );
};
